import { Badge, Button, Group, Stack, Text, Title } from "@mantine/core"
import { useEffect, useState } from "react"
import { QuestionDetails } from "atlas_forecasting_app/components/common/question_details"
import { VisibilityBadge } from "atlas_forecasting_app/components/common/visibility_badge"
import { ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { ForecastDisplay } from "atlas_forecasting_app/components/forecast/forecast_display"
import { ForecastMainDetails } from "atlas_forecasting_app/components/forecast/forecast_main_details"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { NotFoundPage } from "atlas_forecasting_app/pages/not_found_page"

const FORECAST_PUBLISH_CONFIRMATION =
  "Are you sure? Forecasts that are public are viewable by anybody. Once a forecast has been made public it cannot be made private again."

export function ForecastPage({ forecastId }) {
  const [forecast, setForecast] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [notFound, setNotFound] = useState(false)
  const [publishing, setPublishing] = useState(false)

  useEffect(() => {
    let cancelled = false

    const refresh = async () => {
      setLoading(true)
      try {
        const data = await apiFetch(`/api/forecasts/${forecastId}`)
        if (cancelled) return

        setForecast(data.forecast || null)
        setError("")
        setNotFound(false)
      } catch (e) {
        if (cancelled) return
        if (e.status === 404) {
          setForecast(null)
          setNotFound(true)
          setError("")
        } else {
          setError(e.message)
          setNotFound(false)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    refresh()

    return () => {
      cancelled = true
    }
  }, [forecastId])

  const publishForecast = async () => {
    if (!forecast?.can_publish || publishing) return
    if (!window.confirm(FORECAST_PUBLISH_CONFIRMATION)) return

    setPublishing(true)
    try {
      const data = await apiFetch(`/api/forecasts/${forecast.id}/publish`, { method: "PATCH", body: {} })
      setForecast(data.forecast || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setPublishing(false)
    }
  }

  if (loading && !forecast) return <LoadingState label="Loading forecast..." />
  if (notFound) return <NotFoundPage />

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Forecast detail"
        variant="editorial"
        title="Forecast"
        subtitle={forecast ? `Generated ${formatDateTime(forecast.completed_at || forecast.created_at)}` : ""}
        action={
          forecast?.question ? (
            <Button component={RouterLink} to={`/questions/${forecast.question.id}`} variant="light">
              View question
            </Button>
          ) : null
        }
      />

      <ErrorAlert message={error} />

      {forecast ? (
        <>
          <SectionCard
            variant="feature"
            title={
              <Stack gap={4}>
                <Title order={2}>{forecast.question.title}</Title>
                <Text size="sm" c="dimmed">
                  {formatDateTime(forecast.completed_at || forecast.created_at)}
                </Text>
              </Stack>
            }
            action={
              <Group gap="xs">
                <VisibilityBadge publiclyVisible={forecast.question.publicly_visible} visibility={forecast.question.visibility} />
                <Badge variant="light">{forecast.question.question_type}</Badge>
              </Group>
            }
          >
            <QuestionDetails question={forecast.question} showTitle={false} />
          </SectionCard>

          <SectionCard
            variant="editorial"
            title="Forecast result"
            action={
              <Group gap="xs">
                <VisibilityBadge publiclyVisible={forecast.publicly_visible} visibility={forecast.visibility} />
                {forecast.can_publish ? (
                  <Button type="button" variant="light" loading={publishing} onClick={publishForecast}>
                    Make public
                  </Button>
                ) : null}
              </Group>
            }
          >
            <ForecastDisplay question={forecast.question} forecastData={forecast.forecast_data} />
            <ForecastMainDetails
              writeUp={forecast.write_up}
              calculationCode={forecast.calculation_code}
              question={forecast.question}
              contributingForecasts={forecast.contributing_forecasts}
            />
          </SectionCard>
        </>
      ) : null}
    </Stack>
  )
}
